import { tagBuildingFaces } from "building-types/buildingGeomTag/buildingGeomTags";
import { type BuildingGeomOperation } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import { type RoofFace } from "building-types/roofs/roofFace";
import type { RoofPerimeterRail } from "building-types/roofs/roofPerimeterTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { faceArrayViewEntityNew } from "doc/utils/viewEntityTools";
import { aabb2New } from "geom/aabb2Types";
import { aabb3Union } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { Extrusion, Face } from "geom/brep";
import { faceOrNullMoveAlongNormal, faceAABB3, faceOrNullMove, faceCopy, faceReverse } from "geom/brep";
import { buildBSPNode, buildEdgePolygon, isPointInsideBSP } from "geom/bsp";
import type { MaxBSPDepthErrorHandler, NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { extrudeFace, extrudeFaceAlongNormal, extrusionToBRep } from "geom/extrude";
import { offsetPolyLine2, offsetPolyLine2VariableStep } from "geom/offset";
import { planeEval, planeMove, planeNewXY, planeProject, planeProjectPolyLine3 } from "geom/plane";
import {
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2End,
  polyLine2FromAABB2,
  polyLine2Move,
  polyLine2Start,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3OnPlane } from "geom/polyLine3";
import { hasEaveSquaredCut } from "../../elements/roofTrimUtility";

function extrudeRoofTopFace(face: RoofFace | Face, hasSquareCutEave: boolean): Extrusion {
  const faceToExtrude = faceCopy(face.type === "FACE" ? face : face.face);
  const dist = GABLE_ROOF_THICKNESS;
  hasSquareCutEave ? faceOrNullMoveAlongNormal(faceToExtrude, -dist) : faceOrNullMove(faceToExtrude, 0, 0, -dist);

  return hasSquareCutEave ? extrudeFaceAlongNormal(faceToExtrude, dist) : extrudeFace(faceToExtrude, 0, 0, dist);
}

export function extrudeRoofTopFaces(
  faces: RoofFace[] | Face[],
  roofPerimeterRails: RoofPerimeterRail[],
  extrusions: Extrusion[] = [],
): Extrusion[] {
  const hasSquareCutEave = roofPerimeterRails.some(({ eaveType, eaveCutType }) =>
    hasEaveSquaredCut(eaveType, eaveCutType),
  );

  for (let i = 0, l = faces.length; i < l; i++) {
    extrusions.push(extrudeRoofTopFace(faces[i], hasSquareCutEave));
  }

  return extrusions;
}

function extrudeRoofingMaterialFace(
  face: Face,
  roofingMaterialThickness: number,
  roofingMaterialExtension: number,
  perimeterBSP: NodeBSP | null,
  edgeFilter: ((face: Face, loop: PolyLine2, edgeIndex: number) => boolean) | undefined,
  outFaces: Face[] = [],
): Face[] {
  const isEdgeOutside = (x0: number, y0: number, x1: number, y1: number): boolean => {
    return (
      !isPointInsideBSP(perimeterBSP, x0, y0, 0, false, MULTIGABLE_BSP_EPSILON) &&
      !isPointInsideBSP(perimeterBSP, x1, y1, 0, false, MULTIGABLE_BSP_EPSILON)
    );
  };

  face = adjustRoofTopFaceUVs(faceCopy(face));

  const { loops, plane } = face;
  const planeXY = planeNewXY();

  face.loops = loops.map((loop) => {
    const sign = polyLine2WindingDir(loop) === WindingDir.CW ? -1 : 1;
    const edgeCount = polyLine2EdgeCount(loop);
    const offsets: number[] = new Array<number>(edgeCount).fill(0);

    const projectedLoop = offsetPolyLine2(
      planeProjectPolyLine3(planeXY, polyLine3OnPlane(loop, plane)),
      sign * 2 * MULTIGABLE_BSP_EPSILON,
    );

    if (!(projectedLoop.length === loop.length)) {
      console.assert(false);
    }

    for (let i = 0; i < edgeCount; i++) {
      const [testX0, testY0] = polyLine2EdgeStart(projectedLoop, i);
      const [testX1, testY1] = polyLine2EdgeEnd(projectedLoop, i);

      if (isEdgeOutside(testX0, testY0, testX1, testY1) && (!edgeFilter || edgeFilter(face, loop, i))) {
        offsets[i] = sign * roofingMaterialExtension;
      }
    }

    return offsetPolyLine2VariableStep(loop, offsets).pl;
  });

  if (roofingMaterialThickness < DIST_EPSILON) {
    outFaces.push(faceCopy(face));
    outFaces.push(faceReverse(faceCopy(face)));
  } else {
    const extrusion = extrudeFaceAlongNormal(face, roofingMaterialThickness);
    const brep = extrusionToBRep(extrusion);
    outFaces.push(...brep.faces);
  }

  return outFaces;
}

export function extrudeRoofingMaterialFaces(
  roofTopFaces: RoofFace[],
  roofingMaterialThickness: number,
  roofingMaterialExtension: number,
  roofEdges: RoofEdge[],
  maxDepthErrorHandle: MaxBSPDepthErrorHandler,
  outFaces: Face[] = [],
): Face[] {
  const perimeterBSP = buildRoofPerimeterBSP(roofEdges, maxDepthErrorHandle);

  for (let i = 0, l = roofTopFaces.length; i < l; i++) {
    extrudeRoofingMaterialFace(
      roofTopFaces[i].face,
      roofingMaterialThickness,
      roofingMaterialExtension,
      perimeterBSP,
      undefined,
      outFaces,
    );
  }

  return outFaces;
}

const TAG_EXTRUDED_ROOFING_MATERIAL: BuildingGeomOperation = {
  type: "OPERATION",
  operation: "EXTRUDED_ROOFING_MATERIAL",
};

export function extrudeRoofingMaterialFaceEntities(
  faceArrayEntities: FaceArrayViewEntity[],
  roofingMaterialThickness: number,
  roofingMaterialExtension: number,
  maxDepthErrorHandle: MaxBSPDepthErrorHandler,
  outFaceEntities: FaceArrayViewEntity[] = [],
): FaceArrayViewEntity[] {
  const edgeFilter = ({ plane }: Face, loop: PolyLine2, edgeIndex: number): boolean => {
    const i2 = edgeIndex * 2;
    const [, , z0] = planeEval(plane, loop[i2], loop[i2 + 1]);
    const [, , z1] = planeEval(plane, loop[i2 + 2], loop[i2 + 3]);

    return Math.abs(z0 - z1) < DIST_EPSILON;
  };

  for (let i = 0, l = faceArrayEntities.length; i < l; i++) {
    const faceArrayEntity = faceArrayEntities[i];
    const { faces, descriptor, material, pickHandle, materialTransformation } = faceArrayEntity;
    const perimeterBSP = buildRoofTopPerimeterBSP(faces, maxDepthErrorHandle);

    const outFaces: Face[] = [];
    for (let j = 0, lj = faces.length; j < lj; j++) {
      extrudeRoofingMaterialFace(
        faces[j],
        roofingMaterialThickness,
        roofingMaterialExtension,
        perimeterBSP,
        edgeFilter,
        outFaces,
      );
    }

    outFaceEntities.push(
      faceArrayViewEntityNew(
        tagBuildingFaces(outFaces, TAG_EXTRUDED_ROOFING_MATERIAL),
        descriptor,
        material,
        pickHandle,
        materialTransformation,
      ),
    );
  }

  return outFaceEntities;
}

export function adjustRoofTopFaceUVs(face: Face): Face {
  const { loops, plane } = face;
  const { uX, uY, uZ, vX, vY, vZ } = plane;

  const [originU, originV] = planeProject(plane, 0, 0, 0);

  planeMove(plane, uX * originU + vX * originV, uY * originU + vY * originV, uZ * originU + vZ * originV);

  for (let i = 0, l = loops.length; i < l; i++) {
    polyLine2Move(loops[i], -originU, -originV);
  }

  return face;
}

function buildRoofTopPerimeterBSP(
  faces: Face[],
  maxDepthErrorHandle: MaxBSPDepthErrorHandler,
  offset: number = 0,
): NodeBSP | null {
  let aabb: AABB3 | undefined = undefined;
  for (const face of faces) {
    const faceAABB = faceAABB3(face);
    aabb = aabb ? aabb3Union(aabb, faceAABB) : faceAABB;
  }

  if (!aabb) {
    return null;
  }

  const aabb2 = aabb2New(aabb.minX, aabb.minY, aabb.maxX, aabb.maxY);

  const pl2 = offsetPolyLine2(polyLine2FromAABB2(aabb2), offset);

  const bspPolygons: PolygonBSP[] = [];
  for (let i = 0, l = pl2.length; i < l; i += 2) {
    const edgePolygon = buildEdgePolygon(pl2[i], pl2[i + 1], 0, pl2[i + 2], pl2[i + 3], 0, 0, 0, 1);

    if (edgePolygon) {
      bspPolygons.push(edgePolygon);
    }
  }

  return buildBSPNode({
    polygons: bspPolygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });
}

function buildRoofPerimeterBSP(
  roofEdges: RoofEdge[],
  maxDepthErrorHandle: MaxBSPDepthErrorHandler,
  offset: number = 0,
): NodeBSP | null {
  const bspPolygons: PolygonBSP[] = [];

  const perimeter = polyLine2New(roofEdges.length * 2 + 2);
  for (let i = 0, j = 0, l = roofEdges.length; i < l; i++) {
    if (i === 0) {
      const [x, y] = polyLine2Start(roofEdges[i].edge);
      perimeter[j++] = x;
      perimeter[j++] = y;
    }

    const [x, y] = polyLine2End(roofEdges[i].edge);
    perimeter[j++] = x;
    perimeter[j++] = y;
  }

  const offsets = new Array<number>(perimeter.length).fill(0);
  for (let i = 0, l = roofEdges.length; i < l; i++) {
    const { sideOverhang, eaveSlope } = roofEdges[i];

    let edgeOffset = sideOverhang + offset;
    if (eaveSlope !== Number.POSITIVE_INFINITY) {
      edgeOffset = sideOverhang + offset / Math.cos(Math.atan(eaveSlope));
    }

    offsets[i] = edgeOffset;
  }

  const offsetPerimeter = offsetPolyLine2VariableStep(perimeter, offsets).pl;

  if (!(polyLine2EdgeCount(offsetPerimeter) === roofEdges.length)) {
    console.assert(false);
  }

  for (let i = 0, l = roofEdges.length; i < l; i++) {
    const { side } = roofEdges[i];

    if (side === RoofSide.INVALID) {
      continue;
    }

    const [x0, y0] = polyLine2EdgeStart(offsetPerimeter, i);
    const [x1, y1] = polyLine2EdgeEnd(offsetPerimeter, i);

    const edgePolygon = buildEdgePolygon(x0, y0, 0, x1, y1, 0, 0, 0, 1);

    if (edgePolygon) {
      bspPolygons.push(edgePolygon);
    }
  }

  return buildBSPNode({
    polygons: bspPolygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });
}
